import axios from "axios";
import reduxStore from "../redux";
import { jflApi } from "../common/authApi";
import { serverUrl } from "../../ApiRoute";
import { loadHouse } from "../redux/action/CollectiveHouse";
import { houseFromAssets, storeAssetsPath } from "../redux/action/stores";
import { fetchDeviceDetails } from "../redux/action/devices";
import { deviceDetails } from "../common/ApiCall";
import { shareInFlight } from "../common/inFlight";

// POST {energiSync base}/default/device/update
//
// serverUrl.aws already carries the /default stage, so the path appended here is
// /device/update. This service authenticates with a bare `accesstoken` header -
// not `Authorization: Bearer` like the subscription cloud - so the header is
// spelled out rather than borrowed from another module's helper.
//
// The header is sent only when there is a token to send. login.access is set by
// the phone/password sign-in; the PKCE sign-in this app now uses keeps its
// tokens in HttpOnly cookies that JS cannot read, so there is nothing to read
// and the call goes out unauthenticated. That is not a guess in the dark: the
// sibling routes on this same base (/control and /details, in
// common/ApiCall.js) are called with no auth header at all and work, so the
// service is expected to take this one the same way. If it does not, its own
// 401/403 message is what surfaces in the toast - which is more use than
// refusing to make the call.
export const updateDeviceSchedule = async ({ deviceId, writeKey, schedule }) => {
  const token = reduxStore.getState().login?.access;

  try {
    const response = await axios.post(
      `${serverUrl.aws}/device/update`,
      { deviceId, [writeKey]: schedule },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { accesstoken: token } : {}),
        },
      }
    );

    // A 200 can still carry success: false, so the body decides too.
    const failed = response.status !== 200 || response.data?.success === false;

    return {
      success: !failed,
      message:
        response.data?.message ||
        (failed ? "Could not update the schedule" : "Schedule sent to the device"),
    };
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        "Could not update the schedule",
    };
  }
};

// Loads one property's areas and assets into the multi-house slice.
//
// Deliberately not fetchHouseDetails or fetchStoreAssets: both dispatch the
// active-property actions the dashboard reads, and picking a property to review
// here is not the same as changing the property the dashboard opens on. This
// writes the multi-house slice and nothing else.
//
// An already-loaded house is returned from the slice untouched unless `force` is
// passed, which is what the Refresh button does.
export const loadHouseForPicker = async (property, { force = false } = {}) => {
  const houseId = property?.houseAccessCode;

  if (!houseId) {
    return { success: false, message: "This property has no store code." };
  }

  const cached = reduxStore.getState().mutipleHouse?.[houseId];
  if (cached && !force) {
    return { success: true, data: cached, cached: true };
  }

  try {
    const response = await jflApi.get(storeAssetsPath(houseId));
    const house = houseFromAssets(response.data?.data ?? {}, houseId);

    reduxStore.dispatch(loadHouse(houseId, house));

    return { success: true, data: house };
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.error?.errorMessage ||
        error.response?.data?.message ||
        error.message ||
        "Could not load this property",
    };
  }
};

// The schedule objects live on the device document, not in the store assets
// payload, so a row is only right once these have landed.
//
// POST /v1/devices/details takes the whole batch of deviceIds in one call, so
// that is what this uses - keyed by deviceId here because the page reads the
// documents per row. A device missing from the response is simply absent, and
// its row falls back to whatever the assets payload held.
export const loadDeviceDetails = async (deviceIds) => {
  if (deviceIds.length === 0) return { success: true, details: {} };

  const result = await fetchDeviceDetails(deviceIds);

  if (!result.success) {
    return { success: false, message: result.message, details: {} };
  }

  const details = {};

  for (let document of result.data ?? []) {
    if (document?.deviceId) details[document.deviceId] = document;
  }

  return { success: true, details };
};

// Reads one device back after a write, from the same service the write went to -
// so what the row shows next is what the service actually recorded, not what we
// hoped it would. The batch route is the wrong tool here: one device changed.
//
// deviceDetails answers {} for a device it could not read, which is reported as
// null rather than merged as an empty document over a good one.
export const reloadDeviceDetails = async (deviceId) => {
  const document = await deviceDetails(deviceId);

  return document && Object.keys(document).length > 0 ? document : null;
};

// POST {mqttConnectionStatus}/connectionStatus  { deviceId } -> whether the
// device is connected right now, and since when. One device per call.
//
// Quiet on purpose, unlike common/ApiCall's mqttConnectionStatus: this page
// asks for every listed device, and a failed answer just leaves that row on the
// status its device document gives - a toast per device would bury the page.
// Answers null when there is no usable answer.
const readConnectionStatusRequest = async (deviceId) => {
  try {
    const res = await axios.post(`${serverUrl.mqttConnectionStatus}/connectionStatus`, { deviceId });
    const data = res?.data?.success ? res.data.data : null;
    if (typeof data?.connected !== "boolean") return null;
    return { connected: data.connected, connectedSince: Number(data.connectedSince) || null };
  } catch (error) {
    return null;
  }
};

// Identical calls in flight share one request (see common/inFlight).
export const readConnectionStatus = shareInFlight(readConnectionStatusRequest);
